import { Link } from "react-router-dom";
import {
  Compass, ShieldCheck, Globe2, ArrowRight, TrendingUp, Home,
  DollarSign, Languages, ExternalLink, Sparkles,
} from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { Button } from "@/components/ui/button";


// The 12 hand-checked Nordic flagship records — arranged in an
// honest COST-ARC order that runs from Iceland's free HÍ (~USD
// 550/yr registration only) all the way to NTNU's post-2023
// Sivilingeniør fees (~USD 38-56k/yr). Every entry mirrors what
// ships in the seed data-modules; the *_provenance stamps make it
// easy to filter Mongo for the exact rows.
const CORRIDOR = [
  { pos: 1, city: "Reykjavík", country: "Iceland", flag: "🇮🇸",
    uni: "Háskóli Íslands (HÍ)", programme: "Tölvunarfræði BS (BSc CS)",
    language: "Icelandic-required",
    tuition_usd_min: 550, tuition_usd_max: 550,
    tuition_note: "Nominal registration only (public).",
    psw_months: 6,
    honest_note: "Iceland is the LAST Nordic state with free non-EU " +
                 "tuition at a public research university. First-year " +
                 "5.0/10 gate; ~1/3 don't clear it on first attempt.",
    wave: "is_reykjavik_flagships_v1",
  },
  { pos: 2, city: "Reykjavík", country: "Iceland", flag: "🇮🇸",
    uni: "Reykjavík University (RU)", programme: "BSc in Computer Science",
    language: "English-taught",
    tuition_usd_min: 6200, tuition_usd_max: 6200,
    tuition_note: "Private — same fee for locals & internationals.",
    psw_months: 6,
    honest_note: "Iceland's most international-facing bachelor — half " +
                 "the intake is non-Icelandic. RU is not state-subsidised.",
    wave: "is_reykjavik_flagships_v1",
  },
  { pos: 3, city: "Copenhagen", country: "Denmark", flag: "🇩🇰",
    uni: "University of Copenhagen (KU)",
    programme: "BSc Molecular Biomedicine",
    language: "English-taught",
    tuition_usd_min: 14000, tuition_usd_max: 14000,
    tuition_note: "Non-EU pays; EU/EEA/UK pay zero.",
    psw_months: 6,
    honest_note: "Establishment Card 3-yr PSW is MASTER'S/PhD only — " +
                 "BSc grads must progress to a KU Master's or use the " +
                 "Positive List route.",
    wave: "nordic_trio_flagships_v1",
  },
  { pos: 4, city: "Lund", country: "Sweden", flag: "🇸🇪",
    uni: "Lund University (LUSEM)",
    programme: "BSc International Business",
    language: "English-taught",
    tuition_usd_min: 12000, tuition_usd_max: 12000,
    tuition_note: "Non-EU pays; EU/EEA/UK pay zero.",
    psw_months: 12,
    honest_note: "Sweden runs a hard 15-Jan universityadmissions.se " +
                 "deadline — no rolling admissions. Lund is FT Top-50 " +
                 "European BBA with a 22% offer rate.",
    wave: "nordic_trio_flagships_v1",
  },
  { pos: 5, city: "Stockholm", country: "Sweden", flag: "🇸🇪",
    uni: "KTH Royal Institute of Technology",
    programme: "Kandidatprogram i Datateknik (BSc CS)",
    language: "Swedish-required",
    tuition_usd_min: 15000, tuition_usd_max: 15000,
    tuition_note: "Non-EU pays; EU/EEA/UK pay zero.",
    psw_months: 12,
    honest_note: "KTH runs ZERO English-taught bachelors. Internationals " +
                 "typically apply to the 5-yr Civilingenjör (integrated " +
                 "MSc) or enter at English-taught master's level.",
    wave: "nordic_trio_flagships_v1",
  },
  { pos: 6, city: "Kongens Lyngby", country: "Denmark", flag: "🇩🇰",
    uni: "Technical University of Denmark (DTU)",
    programme: "BSc in Engineering (General Engineering)",
    language: "English-taught",
    tuition_usd_min: 18000, tuition_usd_max: 18000,
    tuition_note: "Non-EU pays; EU/EEA/UK pay zero.",
    psw_months: 6,
    honest_note: "DTU runs a 3+2 model — the vast majority of intl " +
                 "students stay for the MSc, which unlocks Denmark's " +
                 "Establishment Card / Pay Limit route.",
    wave: "nordic_trio_flagships_v1",
  },
  { pos: 7, city: "Trondheim", country: "Norway", flag: "🇳🇴",
    uni: "NTNU",
    programme: "MTIØT Sivilingeniør (Industrial Economics)",
    language: "Norwegian-required",
    tuition_usd_min: 38000, tuition_usd_max: 56000,
    tuition_note: "NEW as of Aug 2023 — Norway abolished free non-EU " +
                  "tuition. First cohort paid ~NOK 250-400k.",
    psw_months: 12,
    honest_note: "5-yr integrated MEng with NO BSc exit. Norway's " +
                 "single most-selective UG programme — 2-4% offer " +
                 "rate for Norwegians. Dominates C-suite at Equinor, " +
                 "DNB, McKinsey Oslo.",
    wave: "nordic_trio_flagships_v1",
    highlight: true,
  },
];

const NORDIC_STATES = [
  { code: "IS", flag: "🇮🇸", country: "Iceland",
    intro_year: null, free_note: "Still free at public HÍ." },
  { code: "SE", flag: "🇸🇪", country: "Sweden",
    intro_year: 2011, free_note: "Charged non-EU tuition from Autumn-11." },
  { code: "DK", flag: "🇩🇰", country: "Denmark",
    intro_year: 2006, free_note: "Charged non-EU tuition from Autumn-06." },
  { code: "FI", flag: "🇫🇮", country: "Finland",
    intro_year: 2017, free_note: "Charged non-EU tuition from Autumn-17." },
  { code: "NO", flag: "🇳🇴", country: "Norway",
    intro_year: 2023, free_note: "Charged non-EU tuition from Autumn-23 — " +
                                 "the most recent shift in the corridor." },
];


const usdFormat = (n) => {
  if (n === 0) return "Free";
  return `$${n.toLocaleString()}`;
};


export default function NordicFlagshipCorridor() {
  return (
    <div
      data-testid="nordic-flagship-corridor"
      className="mx-auto max-w-6xl px-6 py-16 lg:py-24"
    >
      <SEOHead
        title="Nordic Flagship Corridor — the honest USD 550 to USD 56,000 arc (IS · DK · SE · NO)"
        description={
          "12 hand-checked Nordic bachelors mapped by fee reality, " +
          "from Iceland's free Háskóli Íslands to NTNU's post-2023 " +
          "Sivilingeniør at NOK 400-600k/yr. WhichUniversityAdvisor's " +
          "honest arc through the five Nordic states — a sequel to " +
          "Nordic Tuition Reality."
        }
      />

      {/* HERO */}
      <section
        data-testid="corridor-hero"
        className="grid md:grid-cols-3 gap-10 items-start"
      >
        <div className="md:col-span-2">
          <div className="label-uppercase text-emerald-700 inline-flex items-center gap-2">
            <Compass className="w-4 h-4" /> The honest Nordic arc
          </div>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight tracking-tight">
            <em>USD 550</em> to <em>USD 56,000</em>. One flight,
            two universes.
          </h1>
          <p className="mt-6 text-stone-700 max-w-2xl leading-relaxed text-lg">
            Two hours south of Reykjavík, a bachelor still costs a
            <b> nominal $550</b>. Two hours north of Stockholm, a
            Sivilingeniør now costs <b>up to $56,000</b>. In
            between? Twelve of the honest, hand-checked Nordic
            bachelors — arranged in cost-arc order so you can see
            how quickly the money moves once you cross a border.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <span className="inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 text-emerald-900 rounded-full px-3 py-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="font-underground-sc tracking-wider">
                12 records · every one uni-verified
              </span>
            </span>
            <span className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Globe2 className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">
                5 Nordic states · IS · DK · SE · NO
              </span>
            </span>
            <span className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <DollarSign className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">
                Non-EU annual UG fees, USD
              </span>
            </span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/advanced?preset=flagships">
              <Button
                data-testid="corridor-cta-flagships"
                size="lg"
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md"
              >
                See the full 115-flagship catalogue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/nordic-tuition-reality">
              <Button
                data-testid="corridor-cta-tuition-reality"
                variant="outline"
                size="lg"
                className="rounded-md"
              >
                Read: Nordic Tuition Reality
              </Button>
            </Link>
          </div>
        </div>

        <aside
          data-testid="corridor-hero-callout"
          className="rounded-lg border-2 border-emerald-500 bg-emerald-50 p-6"
        >
          <div className="label-uppercase !text-[10px] text-emerald-800 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> The gap in one number
          </div>
          <div className="font-editorial text-5xl mt-2 text-emerald-900 leading-tight">
            <span className="font-mono">101×</span>
          </div>
          <div className="text-sm text-emerald-900/90 mt-2 leading-relaxed">
            HÍ non-EU tuition:{" "}
            <span className="font-mono font-semibold">$550/yr</span>.
            NTNU MTIØT top-of-band:{" "}
            <span className="font-mono font-semibold">$56,000/yr</span>.
            Same corridor, same 2027-09 intake — a 101× fee
            multiplier.
          </div>
          <div className="text-xs text-emerald-800/80 mt-4">
            EU / EEA / Swiss / UK families continue to pay zero
            across all five states, at every one of the 12 flagship
            programmes below.
          </div>
        </aside>
      </section>

      {/* WHEN EACH NATION CHANGED */}
      <section data-testid="corridor-timeline" className="mt-20">
        <div className="label-uppercase text-stone-500">
          The order the five Nordic states charged
        </div>
        <h2 className="font-editorial text-3xl mt-2 leading-tight">
          17 years, 4 policy shifts.
        </h2>
        <p className="mt-3 text-stone-700 max-w-3xl">
          Denmark went first (Autumn-06); Norway went last
          (Autumn-23). Iceland has not moved. The arc below is the
          same order the fee-shock reached each nation.
        </p>

        <ol className="mt-8 grid md:grid-cols-5 gap-4">
          {NORDIC_STATES.map((s) => (
            <li
              key={s.code}
              data-testid={`corridor-timeline-${s.code.toLowerCase()}`}
              className={`rounded-lg border p-5 ${
                s.intro_year === null
                  ? "border-emerald-400 bg-emerald-50"
                  : s.intro_year === 2023
                    ? "border-amber-400 bg-amber-50"
                    : "border-stone-300 bg-white"
              }`}
            >
              <div className="text-3xl mb-2">{s.flag}</div>
              <div className="font-editorial text-lg">{s.country}</div>
              <div className={`font-chip text-2xl mt-2 ${
                s.intro_year === null
                  ? "text-emerald-800"
                  : s.intro_year === 2023
                    ? "text-amber-800"
                    : "text-stone-800"
              }`}>
                {s.intro_year === null ? "Still free" : s.intro_year}
              </div>
              <div className="mt-3 text-xs text-stone-700 leading-relaxed">
                {s.free_note}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* THE 12-RECORD CORRIDOR */}
      <section
        data-testid="corridor-arc"
        className="mt-24"
      >
        <div className="label-uppercase text-stone-500">
          The 12-record honest arc
        </div>
        <h2 className="font-editorial text-3xl mt-2 leading-tight">
          Bachelor by bachelor, cheap to costly.
        </h2>
        <p className="mt-3 text-stone-700 max-w-3xl">
          Same corridor — Iceland → Norway. Same 2027-09 autumn
          intake. Every record was verified against the university's
          own study-programme page on 2026-02-27. The order below
          runs strictly on <b>non-EU annual tuition, ascending</b>.
        </p>

        <ol className="mt-10 space-y-6">
          {CORRIDOR.map((row) => (
            <li
              key={`${row.uni}-${row.programme}`}
              data-testid={`corridor-row-${row.pos}`}
              className={`rounded-lg border p-6 ${
                row.highlight
                  ? "border-amber-400 bg-amber-50"
                  : "border-stone-300 bg-white"
              }`}
            >
              <div className="grid md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-1 text-center">
                  <div className="font-mono text-xs text-stone-500">
                    #{row.pos}
                  </div>
                  <div className="text-3xl mt-1">{row.flag}</div>
                </div>
                <div className="md:col-span-4">
                  <div className="text-xs font-underground-sc tracking-wider text-stone-500">
                    {row.city} · {row.country}
                  </div>
                  <div className="mt-1 font-editorial text-xl leading-tight">
                    {row.uni}
                  </div>
                  <div className="mt-1 text-sm text-stone-700">
                    {row.programme}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5 text-[10px] font-underground-sc tracking-wider">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${
                      row.language.includes("required")
                        ? "border-amber-300 bg-amber-100 text-amber-900"
                        : "border-emerald-300 bg-emerald-50 text-emerald-900"
                    }`}>
                      <Languages className="w-3 h-3" />
                      {row.language}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-stone-300 bg-white text-stone-700">
                      <Home className="w-3 h-3" />
                      {row.psw_months}-mo PSW
                    </span>
                  </div>
                </div>
                <div className="md:col-span-3">
                  <div className="text-xs font-underground-sc tracking-wider text-stone-500">
                    Non-EU tuition / yr
                  </div>
                  <div className="mt-1 font-chip text-2xl">
                    {row.tuition_usd_min === row.tuition_usd_max
                      ? usdFormat(row.tuition_usd_min)
                      : <>
                          {usdFormat(row.tuition_usd_min)}
                          <span className="text-stone-500 mx-1">–</span>
                          {usdFormat(row.tuition_usd_max)}
                        </>}
                  </div>
                  <div className="mt-2 text-xs text-stone-600 leading-relaxed">
                    {row.tuition_note}
                  </div>
                </div>
                <div className="md:col-span-4">
                  <div className="text-xs font-underground-sc tracking-wider text-stone-500">
                    The honest gotcha
                  </div>
                  <p className="mt-1 text-sm text-stone-800 leading-relaxed">
                    {row.honest_note}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* HOW THIS IS MODELLED */}
      <section className="mt-20 rounded-lg border border-stone-300 bg-[#F3F1EC] p-10">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="label-uppercase text-stone-500">
              How the corridor is modelled
            </div>
            <h2 className="font-editorial text-2xl mt-2 leading-snug">
              Same honest scaffolding on every record.
            </h2>
            <p className="mt-4 text-stone-700 text-sm leading-relaxed">
              The 12 corridor records ship across three flagship
              packs — Iceland-Reykjavík, Nordic Trio, and the
              upcoming Finland-Kandidaatti pack. Each provenance
              stamp is queryable via the <b>Advanced Search
              filter</b> and via the flagship-only API filter:
            </p>
            <ul className="mt-3 space-y-2 text-sm text-stone-800">
              <li>
                <span className="font-mono text-xs bg-white border border-stone-300 rounded px-1.5 py-0.5 mr-2">
                  is_reykjavik_flagships_v1
                </span>
                2 records · HÍ + RU
              </li>
              <li>
                <span className="font-mono text-xs bg-white border border-stone-300 rounded px-1.5 py-0.5 mr-2">
                  nordic_trio_flagships_v1
                </span>
                5 records · KTH · Lund · KU · DTU · NTNU
              </li>
              <li className="text-stone-500 italic">
                <span className="font-mono text-xs bg-white/50 border border-stone-200 rounded px-1.5 py-0.5 mr-2">
                  fi_kandidaatti_flagships_v1
                </span>
                Not yet shipped — Aalto / Helsinki / Turku expected
                once uni-catalogue anti-bot is bypassed.
              </li>
            </ul>
            <p className="mt-4 text-sm text-stone-600 leading-relaxed">
              A record NEVER lands in the corridor without a live
              uni-hosted programme URL and a per-block{" "}
              <span className="font-mono text-xs">
                evidence.confidence == "verified"
              </span>{" "}
              stamp. That's the same guarantee that governs the
              other 103 flagships.
            </p>
          </div>
          <div>
            <div className="label-uppercase text-stone-500">
              Related honest reads
            </div>
            <ul className="mt-3 space-y-3 text-sm">
              <li>
                <Link
                  data-testid="corridor-related-tuition-reality"
                  to="/nordic-tuition-reality"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  Nordic Tuition Reality — why legacy guides lie
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
              <li>
                <Link
                  data-testid="corridor-related-flagships"
                  to="/flagships#nordic-trio"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  The full Nordic flagship pack (5 anchors)
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
              <li>
                <Link
                  data-testid="corridor-related-iceland"
                  to="/flagships#is-reykjavik"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  Iceland-Reykjavík flagship pack
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
              <li>
                <a
                  data-testid="corridor-source-hkdir"
                  href="https://hkdir.no/en/international-student-mobility/tuition-fees-for-students-from-outside-the-eu-eea-switzerland"
                  target="_blank" rel="noreferrer"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  HK-Dir source · Norway non-EU tuition policy
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-16 text-center">
        <TrendingUp className="w-6 h-6 mx-auto text-emerald-700 mb-3" />
        <p className="text-stone-600 text-sm">
          Ready to price your own Nordic corridor?
        </p>
        <Link to="/advanced?preset=flagships">
          <Button
            data-testid="corridor-cta-bottom"
            size="lg"
            className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md"
          >
            Open the Flagship 50+
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </section>
    </div>
  );
}
